import React, { useState, useRef, useEffect } from "react";
import regionsPh from "./philAPI/regionsPh.json";
import provincePh from "./philAPI/provincePh.json";
import municipalityPh from "./philAPI/municipalityPh.json";
import barangayPh from "./philAPI/barangayPh.json";
import SearchSelect from "../../../../../components/searchSelect/SearchSelect";

const ResidentialAddressSelector = ({ object, onLocationChange, ...props }) => {
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedBarangay, setSelectedBarangay] = useState("");
  const [regionsList, setRegionsList] = useState(regionsPh);
  const [provinceList, setProvinceList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [barangayList, setBarangayList] = useState([]);
  const wrapperRef = useRef();

  const hasInitialized = useRef(false);

  // Prefill on mount or when object changes initially
  useEffect(() => {
    const hasData =
      object && Object.values(object).some((value) => value !== "");
    if (hasData && !hasInitialized.current) {
      const isSame =
        object.recidentialRegion === selectedRegion &&
        object.recidentialProvince === selectedProvince &&
        object.recidentialCity === selectedCity &&
        object.recidentialBrangay === selectedBarangay;

      if (!isSame) {
        setSelectedRegion(object.recidentialRegion || "");
        setSelectedProvince(object.recidentialProvince || "");
        setSelectedCity(object.recidentialCity || "");
        setSelectedBarangay(object.recidentialBrangay || "");
      }

      hasInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object]);

  // Trigger parent callback only after initial load
  useEffect(() => {
    if (hasInitialized.current) {
      const isSame =
        object.recidentialRegion === selectedRegion &&
        object.recidentialProvince === selectedProvince &&
        object.recidentialCity === selectedCity &&
        object.recidentialBrangay === selectedBarangay;

      if (!isSame) {
        const data = {
          recidentialRegion: selectedRegion,
          recidentialProvince: selectedProvince,
          recidentialCity: selectedCity,
          recidentialBrangay: selectedBarangay,
        };
        onLocationChange(data);
      }
    }
  }, [
    selectedRegion,
    selectedProvince,
    selectedCity,
    selectedBarangay,
    onLocationChange,
  ]);

  const clearSelection = (type) => {
    if (type === "region") {
      setSelectedRegion("");
      setSelectedProvince("");
      setSelectedCity("");
      setSelectedBarangay("");
      setProvinceList([]);
      setCityList([]);
      setBarangayList([]);
    } else if (type === "province") {
      setSelectedProvince("");
      setSelectedCity("");
      setSelectedBarangay("");
      setCityList([]);
      setBarangayList([]);
    } else if (type === "city") {
      setSelectedCity("");
      setSelectedBarangay("");
      setBarangayList([]);
    } else if (type === "barangay") {
      setSelectedBarangay("");
    }
  };

  const handleRegionsChange = (valueName, valueCode) => {
    const selectedRegion = valueCode;
    setSelectedRegion(valueName);
    setSelectedProvince("");
    setSelectedCity("");
    setSelectedBarangay("");

    let provinceLists = provincePh.map((province) => ({
      name: province.name,
      code: province.code,
      region: province.regionCode,
    }));

    const cityLists = municipalityPh.map((city) => ({
      name: city.name,
      code: city.code,
      province: city.provinceCode,
      region: city.regionCode,
    }));

    // Handle NCR special case
    if (selectedRegion === "130000000") {
      const filteredCityList = cityLists.filter(
        (city) => city.region === "130000000"
      );

      setCityList(filteredCityList);
      return;
    }

    const filteredProvinceList = provinceLists.filter((province) =>
      province.region.startsWith(selectedRegion)
    );

    setProvinceList(filteredProvinceList);
    setCityList([]);
  };

  const handleProvinceChange = (valueName, valueCode) => {
    const selectedProvince = valueCode;
    setSelectedProvince(valueName);
    setSelectedCity("");
    setSelectedBarangay("");

    const cityLists = municipalityPh.map((city) => ({
      name: city.name,
      code: city.code,
      province: city.provinceCode,
    }));

    const filteredCityList = cityLists.filter(
      (city) => city.province === selectedProvince
    );

    setCityList(filteredCityList);
  };

  const handleCityChange = (valueName, valueCode) => {
    const selectedCity = valueCode;
    setSelectedCity(valueName);
    setSelectedBarangay("");

    const barangayLists = barangayPh.map((barangay) => ({
      name: barangay.name,
      code: barangay.code,
      city: barangay.municipalityCode,
      city1: barangay.cityCode,
      city2: barangay.subMunicipalityCode,
    }));

    const filteredBarangayList = barangayLists.filter(
      (barangay) =>
        barangay.city === selectedCity ||
        barangay.city1 === selectedCity ||
        barangay.city2 === selectedCity
    );

    setBarangayList(filteredBarangayList);
  };

  const handleBarangayChange = (valueName, valueCode) => {
    setSelectedBarangay(valueName);
  };

  return (
    <div ref={wrapperRef}>
      <div className="row">
        <div className="mb-3 col-12 col-md-4 p-1">
          <label className="mb-2 fs-sm" htmlFor="region">
            Regions <span style={{ color: "red" }}>*</span>
          </label>
          <SearchSelect
            {...props}
            focus={true}
            value={selectedRegion.length > 0 ? selectedRegion : ""}
            onClickClear={() => clearSelection("region")}
            required={true}
            onChange={(value) => handleRegionsChange(value.name, value.code)}
            options={regionsList.map((region) => ({
              label: region.name,
              value: { name: region.name, code: region.code },
            }))}
          />
        </div>

        {selectedRegion && selectedRegion !== "NCR" && (
          <div className="mb-3 col-12 col-md-4 p-1">
            <label className="mb-2 fs-sm" htmlFor="province">
              Province <span style={{ color: "red" }}>*</span>
            </label>
            <SearchSelect
              {...props}
              focus={true}
              value={selectedProvince.length > 0 ? selectedProvince : ""}
              onClickClear={() => clearSelection("province")}
              required={true}
              onChange={(value) => handleProvinceChange(value.name, value.code)}
              options={provinceList.map((province) => ({
                label: province.name,
                value: { name: province.name, code: province.code },
              }))}
            />
          </div>
        )}

        {(selectedProvince || selectedRegion === "NCR") && (
          <div className="mb-3 col-12 col-md-4 p-1">
            <label className="mb-2 fs-sm" htmlFor="city">
              City/Municipality <span style={{ color: "red" }}>*</span>
            </label>
            <SearchSelect
              {...props}
              focus={true}
              value={selectedCity.length > 0 ? selectedCity : ""}
              onClickClear={() => clearSelection("city")}
              required={true}
              onChange={(value) => handleCityChange(value.name, value.code)}
              options={cityList.map((city) => ({
                label: city.name,
                value: { name: city.name, code: city.code },
              }))}
            />
          </div>
        )}

        {selectedCity && (
          <div className="mb-3 col-12 col-md-4 p-1">
            <label className="mb-2 fs-sm" htmlFor="barangay">
              Barangay <span style={{ color: "red" }}>*</span>
            </label>
            <SearchSelect
              {...props}
              focus={true}
              value={selectedBarangay.length > 0 ? selectedBarangay : ""}
              onClickClear={() => clearSelection("barangay")}
              required={true}
              onChange={(value) => handleBarangayChange(value.name, value.code)}
              options={barangayList.map((barangay) => ({
                label: barangay.name,
                value: { name: barangay.name, code: barangay.code },
              }))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ResidentialAddressSelector;
